import { HttpTransportType, IHttpConnectionOptions } from '@microsoft/signalr';

import { HttpContext, HttpContextToken, HttpHeaders, type HttpRequest } from '@angular/common/http';

import { BackendLoader } from './api-env';
import { RoleCapacity } from './role-capacity';
import { backendId } from './types';

const HEADER_CHANNEL = 'X-Error-Catcher-Channel';
const HEADER_CAPACITIES = 'X-Error-Catcher-Capacities';
const HEADER_INTERCEPT = 'X-Error-Catcher-Intercept';
const HEADER_REDIRECTION = 'X-Error-Catcher-Redirection';
const HEADER_LANG = 'X-Lang';
const HEADER_GEMS_KEY = 'X-Gems-Key';
const HEADER_API_KEY = 'X-Api-Key';

/**
 * @public
 */
export const API_OPTIONS = new HttpContextToken<ApiOptions>(() => new ApiOptions({ backendId: undefined }));

/**
 * @internal
 */
export interface PartialApiOptionsInterface {
  /**
   * Expected request type for post / patch / put / delete queries to backend
   *
   * _Important:_ it can safely be set for a GET query, as it may fallback to a POST when GET query is too large,
   * in such case, requestType is used, otherwise 'json' is used for GET queries (json is serialized in url directly, body is empty)
   */
  requestType?: 'form' | 'json' | 'raw';

  /**
   * Expected response type from backend
   */
  responseType?: 'blob' | 'text' | 'json';

  /**
   * Expected response type from backend
   */
  responseObserve?: 'response' | 'body';

  /**
   * Alert channel to use for the API call
   */
  channel?: string;

  /**
   * Should the CatchErrorInterceptor intercept the errors and stop its propagation?
   */
  interceptErrors?: boolean;

  /*
   * Should the CatchErrorInterceptor intercept the Multiple Choices 3XX and stop its propagation ?
   */
  interceptRedirection?: boolean;

  /*
   * All mandatory capacities to be granted with, in order to be able to perform a query
   */
  capacities?: RoleCapacity[];

  /**
   * If GET contains too much params, it will be refused by browser or server limit,
   * so on that case we switch to a POST
   */
  simulateGetVerb?: boolean;

  /**
   * Maximum size of GET parameters. If the size exceeds this limit, the request will switch to a POST.
   */
  maxSizeGetParams?: number;

  /**
   * the fields needed to be returned, by default we return all fields
   */
  returnedFields?: string | string[];

  /**
   * Show Spinner progress bar when making api call. Default: true;
   */
  showSpinner?: boolean;

  /**
   * Ignore reload API when 401. Default: false;
   */
  reloadIgnore?: boolean;
}

/**
 * @internal
 */
export interface ApiOptionsInterface extends PartialApiOptionsInterface {
  /**
   * Backend used for the API Implementation
   */
  backendId: backendId;
}

/**
 * @public
 */
export class ApiOptions implements ApiOptionsInterface {
  requestType: 'form' | 'json' | 'raw' = 'form';
  responseType: 'blob' | 'text' | 'json' = 'json';
  responseObserve: 'response' | 'body' = 'body';
  channel = 'general';
  interceptErrors = true;
  interceptRedirection = false;
  backendId: backendId;
  capacities: RoleCapacity[] = [];
  simulateGetVerb: boolean = false;
  maxSizeGetParams: number = 4094;
  returnedFields: string | string[] = undefined;
  showSpinner: boolean = true;
  reloadIgnore: boolean = false;

  constructor(attributes: ApiOptionsInterface) {
    Object.assign(this, attributes);
  }

  static cleanHeaders(headers: HttpHeaders): HttpHeaders {
    headers = headers.delete(HEADER_CHANNEL);
    headers = headers.delete(HEADER_INTERCEPT);
    headers = headers.delete(HEADER_REDIRECTION);
    headers = headers.delete(HEADER_CAPACITIES);
    return headers;
  }

  private buildHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    if (this.channel) {
      headers = headers.set(HEADER_CHANNEL, this.channel);
    }

    if (this.interceptErrors) {
      headers = headers.set(HEADER_INTERCEPT, '');
    }

    if (this.interceptRedirection) {
      headers = headers.set(HEADER_REDIRECTION, '');
    }

    if (this.capacities.length) {
      headers = headers.set(HEADER_CAPACITIES, this.capacities.join(','));
    }

    if (this.simulateGetVerb) {
      headers = headers.append('X-Override-Method', 'GET');
    }

    if (this.returnedFields) {
      headers = headers.append('X-Fields', this.returnedFields);
    }

    return headers;
  }

  toHttpContext() {
    let httpContext = new HttpContext();
    httpContext = httpContext.set(API_OPTIONS, this);
    return httpContext;
  }

  private getBackend(envConfig: BackendLoader) {
    return envConfig.getBackend(this.backendId);
  }

  addBackendUrl(req: HttpRequest<unknown>, envConfig: BackendLoader): HttpRequest<unknown> {
    // Handle Local Requests
    if (!this.backendId) {
      return req;
    }
    const backend = this.getBackend(envConfig);
    if (backend) {
      return req.clone({ url: `${backend.baseUrl}${req.url}` });
    }
    return req;
  }

  addHeaders(
    req: HttpRequest<unknown>,
    backendLoader: BackendLoader,
    authorizationHeader?: string,
    language?: string,
    apiKey?: string,
  ): HttpRequest<unknown> {
    // Handle Local Requests
    if (!this.backendId) {
      return req;
    }
    let headers = this.buildHeaders();
    if (authorizationHeader) headers = headers.set('Authorization', authorizationHeader);
    if (!backendLoader.isSupplyDataUrl(req.url)) headers = headers.set(HEADER_GEMS_KEY, backendLoader.contract.config.token);
    // user language
    headers = headers.set(HEADER_LANG, language || 'en');
    // when running the project with local backends need to add specific headers
    if (backendLoader.isContractLocal(this.backendId) && apiKey) headers = headers.set(HEADER_API_KEY, apiKey);
    return req.clone({ headers });
  }
}

/**
 * @internal
 */
export interface WsOptionsInterface extends IHttpConnectionOptions {
  backendId: backendId;
}
/**
 * @public
 */
export class WsOptions implements WsOptionsInterface {
  skipNegotiation = true;
  transport = HttpTransportType.WebSockets;
  backendId: backendId;
  baseUrl: string;

  constructor(attributes: WsOptionsInterface) {
    Object.assign(this, attributes);
  }

  getHttpConnectionOptions(accessToken: string): IHttpConnectionOptions {
    return {
      accessTokenFactory: () => accessToken,
      skipNegotiation: this.skipNegotiation, // skipNegotiation as we specify WebSockets
      transport: this.transport, // force WebSocket transport
    };
  }

  getBaseUrl(envConfig: BackendLoader) {
    if (!this.backendId) {
      return undefined;
    }
    const backend = envConfig.getBackend(this.backendId);
    if (backend) {
      return backend.baseUrl;
    }
    return undefined;
  }
}
